// src/components/common/ContactForm.js
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackEvent } from '../../services/analyticsService';

function ContactForm() {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null
  const [validationErrors, setValidationErrors] = useState({});

  // Form field labels and messages by language
  const formTexts = {
    en: {
      name: 'Full Name',
      email: 'Email Address',
      subject: 'Subject',
      message: 'Your Message',
      submit: 'Send Message',
      submitting: 'Sending...',
      successTitle: 'Message Sent!',
      successMessage: 'Thank you for your message. We\'ll get back to you soon.',
      errorTitle: 'Oops! Something went wrong.',
      errorMessage: 'Please try again or contact us directly at leon.roese@lucent-webdesign.com',
      required: 'This field is required',
      invalidEmail: 'Please enter a valid email address',
      invalidEmailTitle: 'Invalid Email Address',
      invalidEmailMessage: 'Please check your email address and try again, or contact us directly at leon.roese@lucent-webdesign.com'
    },
    de: {
      name: 'Vollständiger Name',
      email: 'E-Mail-Adresse',
      subject: 'Betreff',
      message: 'Ihre Nachricht',
      submit: 'Nachricht senden',
      submitting: 'Wird gesendet...',
      successTitle: 'Nachricht gesendet!',
      successMessage: 'Vielen Dank für Ihre Nachricht. Wir melden uns bald bei Ihnen.',
      errorTitle: 'Ups! Etwas ist schief gelaufen.',
      errorMessage: 'Bitte versuchen Sie es erneut oder kontaktieren Sie uns direkt unter leon.roese@lucent-webdesign.com',
      required: 'Dieses Feld ist erforderlich',
      invalidEmail: 'Bitte geben Sie eine gültige E-Mail-Adresse ein',
      invalidEmailTitle: 'Ungültige E-Mail-Adresse',
      invalidEmailMessage: 'Bitte überprüfen Sie Ihre E-Mail-Adresse und versuchen Sie es erneut, oder kontaktieren Sie uns direkt unter leon.roese@lucent-webdesign.com'
    }
  };

  const texts = formTexts[language] || formTexts.en;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear status when user starts typing
    if (submitStatus) {
      setSubmitStatus(null);
    }
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
    const { name, email, subject, message } = formData;
    const errors = {};
    
    // Check required fields
    if (!name.trim()) errors.name = texts.required;
    if (!email.trim()) errors.email = texts.required;
    if (!subject.trim()) errors.subject = texts.required;
    if (!message.trim()) errors.message = texts.required;

    // Basic email validation
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = texts.invalidEmail;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // If only email error exists and it's an invalid format
      if (validationErrors.email === texts.invalidEmail && Object.keys(validationErrors).length === 1) {
        setSubmitStatus('invalidEmail');
      } else {
        setSubmitStatus('validation');
      }
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Using Formspree (free and secure service)
      const response = await fetch('https://formspree.io/f/mvgajvqd', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _language: language
        }),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        // Track successful form submission
        trackEvent('Contact', 'Form Submission', 'Success');
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      // Track failed form submission
      trackEvent('Contact', 'Form Submission', 'Error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      <form className="contact-form" onSubmit={handleSubmit} noValidate>
        {/* Name Field */}
        <div className="form-group">
          <label htmlFor="name" className="form-label">
            {texts.name} *
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`form-input ${validationErrors.name ? 'error' : ''}`}
            required
            disabled={isSubmitting}
          />
          {validationErrors.name && (
            <span className="field-error">{validationErrors.name}</span>
          )}
        </div>

        {/* Email Field */}
        <div className="form-group">
          <label htmlFor="email" className="form-label">
            {texts.email} *
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className={`form-input ${validationErrors.email ? 'error' : ''}`}
            required
            disabled={isSubmitting}
          />
          {validationErrors.email && (
            <span className="field-error">{validationErrors.email}</span>
          )}
        </div>

        {/* Subject Field */}
        <div className="form-group">
          <label htmlFor="subject" className="form-label">
            {texts.subject} *
          </label>
          <input
            type="text"
            id="subject"
            name="subject"
            value={formData.subject}
            onChange={handleInputChange}
            className={`form-input ${validationErrors.subject ? 'error' : ''}`}
            required
            disabled={isSubmitting}
          />
          {validationErrors.subject && (
            <span className="field-error">{validationErrors.subject}</span>
          )}
        </div>

        {/* Message Field */}
        <div className="form-group">
          <label htmlFor="message" className="form-label">
            {texts.message} *
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            className={`form-textarea ${validationErrors.message ? 'error' : ''}`}
            rows="6"
            required
            disabled={isSubmitting}
          />
          {validationErrors.message && (
            <span className="field-error">{validationErrors.message}</span>
          )}
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className={`form-submit ${isSubmitting ? 'submitting' : ''}`}
          disabled={isSubmitting}
        >
          {isSubmitting ? texts.submitting : texts.submit}
        </button>

        {/* Status Messages */}
        {submitStatus === 'success' && (
          <div className="status-message success">
            <h4>{texts.successTitle}</h4>
            <p>{texts.successMessage}</p>
          </div>
        )}

        {submitStatus === 'invalidEmail' && (
          <div className="status-message error">
            <h4>{texts.invalidEmailTitle}</h4>
            <p>{texts.invalidEmailMessage}</p>
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="status-message error">
            <h4>{texts.errorTitle}</h4>
            <p>{texts.errorMessage}</p>
          </div>
        )}
      </form>
    </div>
  );
}

export default ContactForm;